import {
  getSamlLogalty,
  requestFrameLogalty,
  SamlRequest,
  SignatureRequest,
} from '../../api/logaltyService';
import { getQuoteFormSummaryQuoteSummary } from '../../api/quoteFormSummaryService';
import { CLIENTS_FOLDER_SEGMENT } from '../../api/fileService';
import {
  REACT_APP_BUCKET_DIGITAL_SIGNATURE,
  REACT_APP_SIGNATURE_BILLING_COST,
  REACT_APP_SIGNATURE_CALLBACK,
  REACT_APP_SIGNATURE_TIME2CLOSE,
  REACT_APP_SIGNATURE_TIME2SAVE,
} from '../../config';

/**
 * Firma de la nueva oferta (OUW-186) — la IDA.
 *
 * Reusa el servicio de firma tal cual (`requestFrameLogalty` / `getSamlLogalty`
 * son genéricos) y los datos del firmante del summary, que es la misma persona
 * que firmó la solicitud. Lo que cambia es QUÉ se firma, y son tres cosas:
 *
 *  - el `ref_id` lleva el id de la oferta, porque el servicio de firma marca
 *    «avisado» por ese identificador: con el del quote, que ya se usó para la
 *    solicitud, la segunda firma nunca avisaría;
 *  - la carpeta es la de la oferta, porque el servicio escribe ahí de vuelta
 *    con nombres fijos y en la del quote pisaría los archivos de la solicitud,
 *    que se custodian diez años;
 *  - se firma UN archivo, el PDF de la oferta.
 *
 * No se reusa la pantalla `/logalty`: vive en las rutas legacy, al terminar
 * manda a la pantalla vieja de felicitaciones, y no hay forma de probarla sin
 * Logalty. Tocarla pondría en riesgo la firma de la solicitud, que está en
 * producción.
 */

/** El nombre con el que el PDF de la oferta se le muestra a quien firma. */
const OFFER_DOCUMENT_SOURCE_NAME = 'oferta.pdf';
const OFFER_DOCUMENT_DISPLAY_NAME = 'Nueva oferta.pdf';

/**
 * DELIBERADAMENTE el mismo mapa que usa hoy la firma de la solicitud
 * (`signaturePage.tsx`), con sus valores tal cual.
 *
 * Está incompleto y se sabe. Lee `quote_form_summary.document_type`, que lo
 * escribe el bridge W4 con los códigos de `asaCodeMaps.ts`: `ine→1`,
 * `military_service_card→2`, `consular_registration→3`,
 * `military_identity_card→4`, `passport→5`, `migration_document→6`. Contra
 * eso, el mapa acierta en `1` y `3`, no tiene `5` (un pasaporte llega a
 * Logalty como `""`), no tiene `2` ni `4`, y traduce `6` como «Pasaporte»
 * cuando es documento migratorio. El análisis completo, con los tres
 * vocabularios y dónde se rompe, está en
 * `docs/intake/LOGALTY-PAYLOAD-FROM-INTAKE.md` §4.
 *
 * Se copia igual porque es lo que corre en producción para la solicitud:
 * cambiarlo a ojo, sin poder probar contra Logalty, arriesgaría la identidad de
 * quien firma un documento con valor legal. Corregirlo es su propio trabajo,
 * y va en los dos lugares a la vez.
 */
const DOCUMENT_TYPE_MX: Record<string, string> = {
  '1': 'INE',
  '3': 'Certificado de Matrícula Consular',
  '6': 'Pasaporte',
};

/** Lo que el portal arma para mandar a alguien a firmar su oferta. */
export type OfferSignatureRequest = {
  quoteId: string;
  offerId: string;
  market?: string;
};

/**
 * offerSignatureRefId: el identificador externo de la firma de la oferta. Tiene
 * que coincidir EXACTO con lo que api-client parsea al volver el veredicto
 * (`ParseOfferSignatureRef`): si no, el aviso se toma por el de una solicitud.
 */
export function offerSignatureRefId(quoteId: string, offerId: string): string {
  return `${quoteId.trim()}:offer:${offerId.trim()}`;
}

/** La carpeta propia de esa oferta, donde el servicio de firma deja todo. */
export function offerSignatureFolder(quoteId: string, offerId: string, market = 'MX'): string {
  return `${market.trim().toUpperCase()}${CLIENTS_FOLDER_SEGMENT}/${quoteId.trim()}/offers/${offerId.trim()}/`;
}

export class OfferSignatureError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OfferSignatureError';
  }
}

/**
 * startOfferSignature deja la transacción lista en Logalty y devuelve el SAML
 * con el que el navegador entra a firmar.
 *
 * Primero pregunta si ya hay uno: si el cliente vuelve a entrar porque cerró la
 * pestaña, tiene que retomar SU transacción y no abrir una nueva — dos
 * transacciones sobre la misma oferta dejarían dos avisos compitiendo.
 */
export async function startOfferSignature(req: OfferSignatureRequest): Promise<string> {
  const quoteId = req.quoteId?.trim();
  const offerId = req.offerId?.trim();
  if (!quoteId || !offerId) {
    throw new OfferSignatureError('Missing quote or offer id');
  }
  const market = (req.market || 'MX').trim().toUpperCase();
  const refId = offerSignatureRefId(quoteId, offerId);

  const existing = await getSamlLogalty({ ref_id: refId } as SamlRequest, 'LOGALTY');
  if (existing?.saml_value) {
    return existing.saml_value;
  }

  const summary = await getQuoteFormSummaryQuoteSummary(quoteId);
  if (!summary) {
    throw new OfferSignatureError('No se pudo cargar los datos del firmante');
  }

  const phone = (summary.phone || '').replace(/\s+/g, '');
  const request: SignatureRequest = {
    ref_id: refId,
    quote_id: quoteId,
    entity_type: 'client_application',
    bucket: `${REACT_APP_BUCKET_DIGITAL_SIGNATURE}`,
    folder: offerSignatureFolder(quoteId, offerId, market),
    subject: '-',
    body: '-',
    synchronous: true,
    frame_url: 'LOGALTY_DIRECT_ACCESS_DOC_IN_FRAME',
    // La vuelta entra por api-client, que con el identificador compuesto sabe
    // que es una oferta y manda al portal en vez de a la pantalla legacy.
    client_url: `${REACT_APP_SIGNATURE_CALLBACK}${refId}`,
    back_url: `${REACT_APP_SIGNATURE_CALLBACK}${refId}`,
    url_back: `${REACT_APP_SIGNATURE_CALLBACK}${refId}`,
    locale: 'es_ES',
    language: 'es',
    sender_email: summary.email || '',
    file_mappings: [
      { search_name: OFFER_DOCUMENT_SOURCE_NAME, target_file_name: OFFER_DOCUMENT_DISPLAY_NAME },
    ],
    contacts: [
      {
        document_type: DOCUMENT_TYPE_MX[summary.document_type] || '',
        document_number: summary.document_number || '',
        first_name: summary.first_name || '',
        middle_name: summary.middle_name || '',
        last_name1: summary.last_name || '',
        last_name2: summary.second_last_name || '',
        country: market,
        email: summary.email || '',
        phone,
        mobile: phone,
        sign_method: 'OTP',
        sign_channel: 'SMS',
      },
    ],
    time2save_days: Number.parseInt(`${REACT_APP_SIGNATURE_TIME2SAVE}`, 10),
    time2close_days: Number.parseInt(`${REACT_APP_SIGNATURE_TIME2CLOSE}`, 10),
    billing_cost: `${REACT_APP_SIGNATURE_BILLING_COST}`,
  };

  const created = await requestFrameLogalty(request, 'LOGALTY');
  if (!created?.saml_value) {
    throw new OfferSignatureError('Logalty no devolvió la asertación de firma');
  }
  return created.saml_value;
}

/**
 * La marca de «este cliente salió a firmar» (OUW-186).
 *
 * Hace falta porque el cliente vuelve de Logalty ANTES que el veredicto: los
 * dos viajan por caminos distintos y el del navegador suele ganar. Sin la
 * marca, al volver ve otra vez el panel de la oferta con el botón de firmar,
 * como si no hubiera pasado nada, y lo más probable es que intente de nuevo.
 *
 * Va en `sessionStorage` y no en la URL: sobrevive la vuelta por el proveedor
 * (misma pestaña, mismo origen) y no depende de que api-client agregue nada al
 * destino. Se limpia sola cuando la solicitud se mueve.
 */
const OFFER_SIGNATURE_MARK = 'ole.portal.offerSignature';

/** Después de esto se deja de esperar: algo salió mal y hay que poder reintentar. */
export const OFFER_SIGNATURE_WAIT_MS = 5 * 60 * 1000;

export function markOfferSignatureStarted(quoteId: string, now = Date.now()): void {
  try {
    window.sessionStorage.setItem(OFFER_SIGNATURE_MARK, JSON.stringify({ quoteId, at: now }));
  } catch {
    // Navegación privada o almacenamiento bloqueado: se pierde el aviso de
    // espera, no el trámite. No vale la pena romper la firma por esto.
  }
}

export function clearOfferSignatureMark(): void {
  try {
    window.sessionStorage.removeItem(OFFER_SIGNATURE_MARK);
  } catch {
    /* idem */
  }
}

/** ¿Este cliente acaba de salir a firmar ESTA solicitud? */
export function isAwaitingOfferSignature(quoteId: string, now = Date.now()): boolean {
  try {
    const raw = window.sessionStorage.getItem(OFFER_SIGNATURE_MARK);
    if (!raw) return false;
    const mark = JSON.parse(raw) as { quoteId?: string; at?: number };
    if (mark.quoteId !== quoteId || typeof mark.at !== 'number') return false;
    return now - mark.at < OFFER_SIGNATURE_WAIT_MS;
  } catch {
    return false;
  }
}
